/**
 * Route-mounted view for RMA detail, creation, editing, and processing.
 *
 * Fetches the RMA when rma_id is present and delegates rendering to the
 * appropriate sub-component based on the `mode` prop.
 *
 * Contains:
 * - ReturnView          — default export (view mode, staff routes)
 * - NewReturnView / EditReturnView / ProcessReturnView — mode-bound staff routes
 * - CustomerReturnView  — plant-scoped view mode (self-seeds customer + plant context)
 */

import { Box, CircularProgress, Typography } from '@mui/material';
import { Map } from 'immutable';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { fetchRma } from '@sophocles/entities/Rma';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';

// Imported even though not yet written — other agents are creating these
// against the prop contract documented below.
// Props contract:
//   CreateReturnForm: no props for create mode; rma={Map} for edit mode
//   ProcessReturnForm: rma={Map}
import ReturnSummary from './components/ReturnSummary';
import CreateReturnForm from './forms/CreateReturnForm';
import InvoiceReturnForm from './forms/InvoiceReturnForm';
import ProcessReturnForm from './forms/ProcessReturnForm';
import QuickShipReturnForm from './forms/QuickShipReturnForm';

/**
 * @param {'view'|'create'|'edit'|'process'|'quickship'|'invoice'} mode
 * @param {Object} props - passed through to AppNav (match, location, notice from RequireAuth, etc.)
 */
function ReturnView({ mode = 'view', ...props }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { rma_id: rmaId, sitegroup_id: sitegroupId, cust_id: custId } = useParams();

  const [rma, setRma] = useState(Map());
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const needsRma = mode !== 'create';

  const doFetch = useCallback(() => {
    if (!rmaId || !needsRma) return;
    setLoading(true);
    setFetchError(null);
    dispatch(fetchRma(rmaId))
      .then(result => {
        setRma(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setFetchError(t('rma.fetchError', 'Unable to load RMA. Please try again.'));
      });
  }, [dispatch, rmaId, needsRma, t]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  const rmaNumber = rma.get('rma_number', null);

  const pageTitle = rmaNumber
    ? `RMA #${rmaNumber}`
    : mode === 'create'
      ? t('rma.newRma', 'New RMA')
      : t('rma.rma', 'RMA');

  // Customer-scoped routes live under a plant so use plant_rmas; staff routes
  // highlight the "RMA List" nav entry. We determine this by the presence of
  // sitegroup_id in the URL params.
  const pageIdentifier = sitegroupId ? 'plant_rmas' : 'returns_rmas';

  // Breadcrumb back to the matching RMA list (plant-scoped or staff),
  // prefixed with the auto scope trail so the plant-scoped variant keeps its
  // Customer > Plant context (explicit crumbs replace the auto trail
  // wholesale; on staff routes the auto trail is empty and nothing changes).
  const autoCrumbs = useBreadcrumbs();
  const rmaListPath = sitegroupId ? `/c/${custId}/plant/${sitegroupId}/support/rmas` : '/rmas';
  const breadcrumbs = [...autoCrumbs, { label: t('support.rmaList', 'RMAs'), to: rmaListPath }, { label: pageTitle }];

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (fetchError) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography color="error">{fetchError}</Typography>
        </Box>
      );
    }

    if (mode === 'create') {
      return <CreateReturnForm />;
    }

    if (mode === 'edit') {
      return <CreateReturnForm rma={rma} />;
    }

    if (mode === 'process') {
      return <ProcessReturnForm rma={rma} />;
    }

    if (mode === 'quickship') {
      return <QuickShipReturnForm rma={rma} />;
    }

    if (mode === 'invoice') {
      return <InvoiceReturnForm rma={rma} />;
    }

    // mode === 'view'
    return <ReturnSummary rma={rma} onRefresh={doFetch} />;
  };

  return (
    <AppNav notFullHeight loading={loading} page={pageIdentifier} {...props}>
      <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TitleBar breadcrumbs={breadcrumbs} title={pageTitle} />
        {renderContent()}
      </Box>
    </AppNav>
  );
}

const withMode = mode => {
  const ModeBoundReturnView = props => <ReturnView mode={mode} {...props} />;
  ModeBoundReturnView.displayName = `ReturnView(${mode})`;
  return ModeBoundReturnView;
};

// Staff routes — view mode is the default export; create/edit/process are
// mode-bound so routes.js can mount them without inline wrapper components.
// No HOC: staff views are plain components (user context comes from useUser()).
export default ReturnView;
export const NewReturnView = withMode('create');
export const EditReturnView = withMode('edit');
export const ProcessReturnView = withMode('process');
export const QuickShipReturnView = withMode('quickship');
export const InvoiceReturnView = withMode('invoice');

// Customer-scoped plant route — self-seeds customer + plant context from
// the route's cust_id / sitegroup_id URL params (flat / DCE-lazy route,
// no ScopeContext provider above it).
export function CustomerReturnView(props) {
  const { cust_id, sitegroup_id } = useParams();

  const { loading: customerLoading } = useCustomer(Number(cust_id));
  const { siteGroup, loading: siteGroupLoading } = useSiteGroup(Number(sitegroup_id));

  // Gate on context readiness — mirror PlantHOC's render guard.
  const siteGroupReady = siteGroup && siteGroup.get('id') == sitegroup_id;
  if (!siteGroupReady || siteGroupLoading || customerLoading) {
    return <LoadingScreen />;
  }

  return <ReturnView {...props} />;
}
